/**
 * FilterControls – renders filter inputs from a FilterConfig[] array.
 *
 * This is the shared "just the filters" component that can be embedded in:
 * - FilterSidebar (list pages, with collapsible sections)
 * - A static sidebar panel (dynamic list page, always expanded)
 */
import { useState, useCallback, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { FilterChecklist } from '@/components/filters/FilterChecklist';
import { FilterDateRange } from '@/components/filters/FilterDateRange';
import { FilterRange } from '@/components/filters/FilterRange';
import { FilterSelect } from '@/components/filters/FilterSelect';
import {
  type FilterConfig,
  type FilterNegations,
  type FilterOption,
  type FilterValue,
  isDateRange,
  isRange,
  isSelect,
  filterActiveCount,
} from '@/types/filters.types';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export interface FilterControlsProps {
  filters: FilterConfig[];
  values: Record<string, FilterValue>;
  options: Record<string, FilterOption[]>;
  onChange: (filterKey: string, value: FilterValue) => void;
  onClear?: (filterKey: string) => void;
  negations?: FilterNegations;
  onNegateChange?: (filterKey: string, negate: boolean) => void;
  searchCommitMode?: 'change' | 'blur-or-enter';
  /**
   * When true, all filter sections are always expanded and section headers
   * are non-interactive (no collapse chevron). Ideal for the dynamic list page.
   */
  alwaysExpanded?: boolean;
  /** localStorage key prefix for persisting section collapsed state */
  storageKey?: string;
  lockedKeys?: string[];
  lockedLabel?: string;
}

function getDefaultValue(config: FilterConfig): FilterValue {
  if (config.type === 'daterange') return { from: '', to: '' };
  if (config.type === 'range') return { min: config.min ?? 0, max: config.max ?? 120 };
  return [];
}

export function FilterControls({
  filters,
  values,
  options,
  onChange,
  onClear,
  negations,
  onNegateChange,
  searchCommitMode = 'change',
  alwaysExpanded = false,
  storageKey,
  lockedKeys,
  lockedLabel = 'Global',
}: FilterControlsProps) {
  const lockedSet = new Set(lockedKeys ?? []);

  const [sectionState, setSectionState] = useLocalStorage<Record<string, boolean>>(
    storageKey ? `ftp-filter-sections-${storageKey}` : '__unused-sections__',
    {},
  );

  const isSectionCollapsed = useCallback(
    (key: string, hasActive: boolean) => {
      if (alwaysExpanded) return false;
      const stored = sectionState[key];
      if (typeof stored === 'boolean') return stored;
      return !hasActive;
    },
    [sectionState, alwaysExpanded],
  );

  const toggleSection = useCallback(
    (key: string, hasActive: boolean) => {
      if (alwaysExpanded) return;
      setSectionState((current) => {
        const wasCollapsed =
          typeof current[key] === 'boolean' ? current[key] : !hasActive;
        return { ...current, [key]: !wasCollapsed };
      });
    },
    [setSectionState, alwaysExpanded],
  );

  const [draftSearchValues, setDraftSearchValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraftSearchValues((current) => {
      const next: Record<string, string> = {};
      let changed = false;

      for (const config of filters) {
        if (config.type !== 'search') continue;
        const value = values[config.key];
        const nextValue = isSelect(value) ? value[0] ?? '' : '';
        next[config.key] = nextValue;
        if ((current[config.key] ?? '') !== nextValue) changed = true;
      }

      if (!changed && Object.keys(current).length === Object.keys(next).length) {
        return current;
      }
      return next;
    });
  }, [filters, values]);

  const commitSearchValue = useCallback(
    (filterKey: string, nextValue: string) => {
      const trimmed = nextValue.trim();
      onChange(filterKey, trimmed ? [trimmed] : []);
    },
    [onChange],
  );

  return (
    <div className="space-y-5">
      {filters.map((config) => {
        const val = values[config.key] ?? getDefaultValue(config);
        const count = filterActiveCount(val);
        const isLocked = lockedSet.has(config.key);
        const hasActive = isLocked || count > 0;
        const sectionCollapsible = !alwaysExpanded && config.type !== 'search';
        const sectionExpanded = !sectionCollapsible || !isSectionCollapsed(config.key, hasActive);
        const appliedSearchValue = config.type === 'search' && isSelect(val) ? val[0] ?? '' : '';
        const currentSearchValue =
          config.type === 'search'
            ? searchCommitMode === 'blur-or-enter'
              ? (draftSearchValues[config.key] ?? appliedSearchValue)
              : appliedSearchValue
            : '';

        const headerBadge = isLocked ? (
          <span className="text-[10px] font-medium text-blue-600" title="Locked by global facility selector">
            {lockedLabel}
          </span>
        ) : config.type === 'search' ? null : config.type === 'range' ? (
          count === 0 ? (
            <span className="text-[10px] text-blue-600">{config.allLabel ?? 'ALL'}</span>
          ) : (
            <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold tabular-nums">
              {count}
            </span>
          )
        ) : count > 0 ? (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold tabular-nums">
            {count}
          </span>
        ) : (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-slate-400 text-[10px] font-semibold tabular-nums">
            {count}
          </span>
        );

        return (
          <div key={config.key} className="space-y-2">
            {sectionCollapsible ? (
              <button
                type="button"
                onClick={() => toggleSection(config.key, hasActive)}
                className="flex w-full items-center justify-between gap-2 text-left"
                aria-expanded={sectionExpanded}
              >
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <ChevronDown
                    className={cn(
                      'h-3 w-3 text-slate-400 transition-transform',
                      !sectionExpanded && '-rotate-90',
                    )}
                  />
                  {config.label}
                </span>
                {headerBadge}
              </button>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  {config.label}
                </span>
                {headerBadge}
              </div>
            )}

            {sectionExpanded && (
              <div
                className={cn(isLocked && 'pointer-events-none select-none opacity-60')}
                aria-disabled={isLocked || undefined}
              >
                {config.type === 'daterange' && isDateRange(val) && (
                  <FilterDateRange
                    value={val}
                    onChange={(v) => onChange(config.key, v)}
                    onClear={onClear ? () => onClear(config.key) : undefined}
                  />
                )}

                {config.type === 'select' && isSelect(val) && (
                  config.multi ? (
                    <FilterChecklist
                      value={val}
                      options={options[config.key] ?? []}
                      onChange={(v) => onChange(config.key, v)}
                      negate={negations?.[config.key] ?? false}
                      onNegateChange={
                        onNegateChange
                          ? (neg) => onNegateChange(config.key, neg)
                          : undefined
                      }
                      searchable={config.searchable ?? (options[config.key]?.length ?? 0) > 6}
                      groupSelect={config.groupSelect}
                      collapsibleGroups={config.collapsibleGroups}
                    />
                  ) : (
                    <FilterSelect
                      value={val}
                      options={options[config.key] ?? []}
                      allLabel={config.allLabel}
                      onChange={(v) => onChange(config.key, v)}
                    />
                  )
                )}

                {config.type === 'range' && isRange(val) && (
                  <FilterRange
                    value={val}
                    min={config.min ?? 0}
                    max={config.max ?? 120}
                    onChange={(v) => onChange(config.key, v)}
                    resetLabel={config.allLabel}
                  />
                )}

                {config.type === 'search' && (
                  <div className="relative">
                    <Input
                      placeholder={config.allLabel ?? 'Search...'}
                      className="h-8 text-xs !pl-2 pr-7"
                      value={currentSearchValue}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (searchCommitMode === 'blur-or-enter') {
                          setDraftSearchValues((current) => ({ ...current, [config.key]: nextValue }));
                          return;
                        }
                        onChange(config.key, nextValue ? [nextValue] : []);
                      }}
                      onKeyDown={(e) => {
                        if (searchCommitMode === 'blur-or-enter' && e.key === 'Enter') {
                          commitSearchValue(config.key, currentSearchValue);
                        }
                      }}
                      onBlur={() => {
                        if (searchCommitMode === 'blur-or-enter') {
                          commitSearchValue(config.key, currentSearchValue);
                        }
                      }}
                    />
                    {currentSearchValue !== '' && (
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (searchCommitMode === 'blur-or-enter') {
                            setDraftSearchValues((current) => ({ ...current, [config.key]: '' }));
                          }
                          onChange(config.key, []);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
